import { moment } from '@eventbrite/date';
import { ISO_8601_DATE_FORMAT } from '@eventbrite/datetime';
import { Dropdown } from '@eventbrite/eds-containers';
import { Icon } from '@eventbrite/eds-icon';
import { IconButton } from '@eventbrite/eds-icon-button';
import { CalendarChunky, CrossChunky } from '@eventbrite/eds-iconography';
import {
    InputField,
    MIN_ASIDE_SPACING_SUFFIX,
} from '@eventbrite/eds-input-field';
import { DEFAULT_INPUT_DISPLAY_FORMAT } from '@eventbrite/eds-utils';
import { GenericLazyString, gettext } from '@eventbrite/i18n';
import React, { FunctionComponent, useState } from 'react';
import { DatePicker } from './DatePicker/DatePicker';
import { CalendarDayInfo, getDatesBetweenEvents } from './utils';

import './accessibleDatePicker.scss';

export interface AccessibleDatePickerProps {
    childEvents: CalendarDayInfo[];
    localeCode: string;
    name: string;
    onClear: () => void;
    onMonthChange: (month: Date) => void;
    onSelect: (date: Date) => void;
    tabIndex?: string | undefined;
}

interface DateInputProps {
    id: string;
    label: GenericLazyString;
    minAsideSpacing: string;
    name: string;
    onClearDate: () => void;
    onChange: (value: string) => void;
    onFocus: () => void;
    readOnly: boolean;
    tabIndex: string;
    value: string;
}

export const DateInputDefaultPrefix: FunctionComponent = () => (
    <Icon type={<CalendarChunky />} color="grey-800" />
);

export const DateInputDefaultSuffix: FunctionComponent<{
    onClearDateClick: () => void;
}> = ({ onClearDateClick }) => (
    <IconButton
        iconType={<CrossChunky />}
        title={gettext('Clear selected date and close calendar').toString()}
        aria-label={gettext(
            'Clear selected date and close calendar',
        ).toString()}
        onClick={onClearDateClick}
    />
);

export const DateInput = (props: DateInputProps) => (
    <InputField
        prefix={<DateInputDefaultPrefix />}
        suffix={<DateInputDefaultSuffix onClearDateClick={props.onClearDate} />}
        type={'text'}
        id={props.id}
        label={props.label}
        minAsideSpacing={props.minAsideSpacing}
        name={props.name}
        onChange={props.onChange}
        onFocus={props.onFocus}
        readOnly={props.readOnly}
        value={props.value}
        tabIndex={props.tabIndex}
    />
);

export const AccessibleDatePicker = ({
    childEvents,
    localeCode,
    name,
    onClear,
    onMonthChange,
    onSelect,
    tabIndex = '0',
}: AccessibleDatePickerProps) => {
    const [inputValue, setInputValue] = useState('');
    const [focusedInput, setFocusedInput] = useState(false);
    const [selectedDate, setSelectedDate] = useState<Date>();

    const inputId = 'accessible-date-picker-input';

    const datesWithoutEvent = getDatesBetweenEvents(childEvents);
    const firstLiveEvent = new Date(childEvents[0]?.date);
    const lastLiveEvent = new Date(childEvents[childEvents.length - 1]?.date);

    const _formatInputValue = (value: string) =>
        value && moment(value).format(DEFAULT_INPUT_DISPLAY_FORMAT);

    const _handleClearDate = () => {
        setInputValue('');
        setSelectedDate(undefined);
        if (onClear) {
            onClear();
        }
        // Agreed on EB-231683: the clear button closes the calendar too
        setFocusedInput(false);
    };

    const _handleInputChange = (value: string) => {
        setInputValue(value);
    };

    const _handleMonthChange = (month: Date) => {
        onMonthChange(month);
    };

    const _handleOnSelect = (date: Date) => {
        const formattedDate = moment(date).format(ISO_8601_DATE_FORMAT);
        const formatInputValue = _formatInputValue(formattedDate);

        setSelectedDate(date);
        setInputValue(formatInputValue);
        onSelect(date);
        setFocusedInput(false);
    };

    const _handleClickOutside = (event: React.MouseEvent<HTMLInputElement>) => {
        if ((event?.target as HTMLInputElement)?.id === inputId) {
            return;
        }
        setFocusedInput(false);
    };

    const _handleOnInputDateFocus = () => {
        setFocusedInput(true);
    };

    const datesWithEvents = childEvents.map((event) => event.date);

    const calendarComponent = focusedInput ? (
        <div
            className={'calendar-dropdown'}
            role="dialog"
            aria-modal="true"
            aria-label={gettext('Date Picker Dialog').toString()}
        >
            <Dropdown onClickOutside={_handleClickOutside} align="left">
                <DatePicker
                    defaultMonth={selectedDate}
                    disabled={datesWithoutEvent}
                    fromDate={firstLiveEvent}
                    localeCode={localeCode}
                    mode="single"
                    modifiers={{
                        booked: datesWithEvents,
                    }}
                    onMonthChange={(month) => _handleMonthChange(month)}
                    onSelect={(_day, selectedDay) =>
                        _handleOnSelect(selectedDay)
                    }
                    selected={selectedDate}
                    toDate={lastLiveEvent}
                />
            </Dropdown>
        </div>
    ) : null;

    return (
        <>
            <DateInput
                id={inputId}
                label={gettext('Filter by Date')}
                minAsideSpacing={MIN_ASIDE_SPACING_SUFFIX}
                name={name}
                onChange={_handleInputChange}
                onClearDate={_handleClearDate}
                onFocus={_handleOnInputDateFocus}
                readOnly={true}
                value={inputValue}
                tabIndex={tabIndex}
            />
            {calendarComponent}
        </>
    );
};
